import {
  computed,
  onBeforeUpdate,
  onUpdated,
  onMounted,
  onBeforeUnmount,
  reactive,
  watch,
} from 'vue';
import { useStore } from 'dashboard/composables/store';
import { captureConversationHistoryWindow } from 'dashboard/store/modules/conversations/helpers/historyRequests';
import {
  rememberConversationPosition,
  getConversationPositionRestore,
  finishConversationPositionRestore,
} from 'dashboard/store/modules/conversations/helpers/historyPosition';

// Geometry belongs to the live view; only a scalar bookmark survives access clearing.
export const useConversationHistoryScroll = panelRef => {
  const store = useStore();
  const context = {
    rootState: store.state,
    rootGetters: store.getters,
    state: store.state.conversations,
  };
  const chat = computed(() => store.getters.getSelectedChat);
  const panel = () => panelRef.value?.$el;
  let anchor = null;
  let pending = null;
  let resizeObserver;
  let mutationObserver;
  const observed = new Set();
  let expectedScrollTop = null;
  let mounted = false;
  let selection = {};
  let targetScroll = null;
  let mountedPanel;
  const manualScrollEvents = ['wheel', 'pointerdown', 'keydown'];
  const state = reactive({
    atEnd: true,
    isRestoring: false,
    hasRestoredPosition: false,
  });

  const capture = () => {
    const viewport = panel();
    if (!viewport) return null;
    const top = viewport.getBoundingClientRect().top;
    const row = [...viewport.querySelectorAll('[data-message-id]')].find(
      element => element.getBoundingClientRect().bottom > top
    );
    return row
      ? {
          anchorId: row.dataset.messageId,
          offset: row.getBoundingClientRect().top - top,
        }
      : null;
  };
  const remember = () => {
    if (state.isRestoring || !chat.value.dataFetched) return;
    const readingAnchor = anchor || capture();
    if (!state.atEnd && !readingAnchor) return;
    rememberConversationPosition(context, {
      conversationId: chat.value.id,
      anchorId: readingAnchor?.anchorId,
      offset: readingAnchor?.offset,
      atEnd: state.atEnd,
    });
  };
  const restore = bookmark => {
    const viewport = panel();
    if (!viewport || !chat.value.dataFetched) return false;
    if (bookmark?.atEnd ?? state.atEnd) {
      viewport.scrollTop = viewport.scrollHeight;
      anchor = capture();
    } else {
      const target = bookmark || anchor;
      const row = [...viewport.querySelectorAll('[data-message-id]')].find(
        element => element.dataset.messageId === target?.anchorId
      );
      if (!row) return false;
      const delta =
        row.getBoundingClientRect().top -
        viewport.getBoundingClientRect().top -
        target.offset;
      if (delta) viewport.scrollTop += delta;
      // Newly inserted rows can enter the viewport before their heights settle.
      // Keep the requested row until a user scroll chooses another one.
      anchor = { anchorId: target.anchorId, offset: target.offset };
    }
    expectedScrollTop = viewport.scrollTop;
    return true;
  };
  const update = () => {
    const viewport = panel();
    if (viewport && resizeObserver) {
      const targets = new Set([viewport, ...viewport.children]);
      observed.forEach(element => {
        if (!targets.has(element)) {
          resizeObserver.unobserve(element);
          observed.delete(element);
        }
      });
      targets.forEach(element => {
        if (!observed.has(element)) {
          resizeObserver.observe(element);
          observed.add(element);
        }
      });
    }
    if (targetScroll) {
      remember();
      return;
    }
    const position = getConversationPositionRestore(context, chat.value.id);
    state.isRestoring = Boolean(position);
    if (position) {
      if (restore(position)) {
        state.atEnd = position.atEnd;
        state.hasRestoredPosition = true;
        finishConversationPositionRestore(context, position);
        state.isRestoring = false;
        remember();
      }
    } else {
      restore(pending);
      remember();
    }
    pending = null;
  };
  const onScroll = () => {
    const viewport = panel();
    if (!viewport || state.isRestoring) return;
    if (targetScroll) {
      if (Math.abs(viewport.scrollTop - targetScroll.startTop) > 1)
        targetScroll.moved = true;
      remember();
      return;
    }
    if (
      expectedScrollTop !== null &&
      Math.abs(viewport.scrollTop - expectedScrollTop) < 1
    ) {
      expectedScrollTop = null;
      return;
    }
    expectedScrollTop = null;
    state.atEnd =
      !chat.value.historyHasMoreAfter &&
      viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <= 2;
    anchor = capture();
    remember();
  };
  const cancelTargetScroll = () => {
    if (!targetScroll) return;
    targetScroll = null;
    const viewport = panel();
    viewport.scrollTo({ top: viewport.scrollTop, behavior: 'instant' });
  };
  const onManualScroll = () => {
    if (!targetScroll) return;
    cancelTargetScroll();
    pending = null;
    expectedScrollTop = null;
    onScroll();
  };
  const onScrollEnd = () => {
    if (!targetScroll?.moved) return;
    targetScroll = null;
    anchor = capture();
    expectedScrollTop = null;
    const viewport = panel();
    state.atEnd =
      !chat.value.historyHasMoreAfter &&
      viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <= 2;
    remember();
    update();
  };
  const navigate = () => {
    cancelTargetScroll();
    const needsFreshWindow =
      chat.value.dataFetched !== true ||
      Boolean(getConversationPositionRestore(context, chat.value.id));
    store.dispatch('cancelConversationHistoryRestore', chat.value.id);
    pending = null;
    anchor = null;
    state.atEnd = false;
    state.isRestoring = false;
    state.hasRestoredPosition = false;
    return needsFreshWindow;
  };
  const followEnd = () => {
    cancelTargetScroll();
    state.atEnd = true;
    pending = null;
    restore({ atEnd: true });
    remember();
  };
  const reconcileEnd = () => {
    const viewport = panel();
    if (
      mounted &&
      viewport &&
      chat.value.dataFetched === true &&
      !state.isRestoring &&
      !targetScroll &&
      !chat.value.historyHasMoreAfter &&
      viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <= 2
    )
      followEnd();
  };
  const scrollToTarget = element => {
    cancelTargetScroll();
    const viewport = panel();
    const supportsScrollEnd = 'onscrollend' in viewport;
    const targetTop =
      viewport.scrollTop +
      element.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top;
    const destination = Math.max(
      0,
      Math.min(targetTop, viewport.scrollHeight - viewport.clientHeight)
    );
    if (supportsScrollEnd && Math.abs(destination - viewport.scrollTop) > 1)
      targetScroll = { startTop: viewport.scrollTop, moved: false };
    anchor = null;
    pending = null;
    expectedScrollTop = null;
    state.atEnd = false;
    element.scrollIntoView({
      behavior: supportsScrollEnd ? 'smooth' : 'instant',
    });
    if (targetScroll) {
      targetScroll.moved =
        Math.abs(viewport.scrollTop - targetScroll.startTop) > 1;
    } else {
      anchor = capture();
      expectedScrollTop = viewport.scrollTop;
      remember();
    }
  };
  watch(
    () => chat.value.id,
    () => {
      cancelTargetScroll();
      selection = {};
      anchor = null;
      pending = null;
      expectedScrollTop = null;
      state.atEnd = !chat.value.historyHasMoreAfter;
      state.hasRestoredPosition = false;
      state.isRestoring = Boolean(
        getConversationPositionRestore(context, chat.value.id)
      );
      resizeObserver?.disconnect();
      observed.clear();
    },
    { immediate: true, flush: 'sync' }
  );
  onBeforeUpdate(() => {
    if (mounted && !state.isRestoring && !targetScroll)
      pending = { ...(anchor || capture()), atEnd: state.atEnd };
  });
  onUpdated(update);
  onMounted(() => {
    mounted = true;
    mountedPanel = panel();
    mountedPanel.addEventListener('scrollend', onScrollEnd);
    manualScrollEvents.forEach(event =>
      mountedPanel.addEventListener(event, onManualScroll, { passive: true })
    );
    resizeObserver = new ResizeObserver(update);
    mutationObserver = new MutationObserver(update);
    mutationObserver.observe(panel(), {
      childList: true,
      subtree: true,
      characterData: true,
    });
    update();
  });
  onBeforeUnmount(() => {
    mounted = false;
    selection = {};
    targetScroll = null;
    mountedPanel?.removeEventListener('scrollend', onScrollEnd);
    manualScrollEvents.forEach(event =>
      mountedPanel?.removeEventListener(event, onManualScroll)
    );
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    observed.clear();
  });
  return Object.assign(state, {
    onScroll,
    navigate,
    followEnd,
    reconcileEnd,
    scrollToTarget,
    captureWindow: () => {
      const lifetime = selection;
      const isCurrent = captureConversationHistoryWindow(
        context,
        chat.value.id
      );
      return () => mounted && selection === lifetime && isCurrent();
    },
  });
};
